import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import {
  Card, Grid, Box, Snackbar, Alert, Icon, Dialog, DialogTitle, DialogContent,
  DialogActions, Tooltip, CircularProgress, LinearProgress,
  Modal, FormControlLabel, Checkbox,
} from "@mui/material";
import colors from "assets/theme/base/colors";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import { styled } from '@mui/material/styles';
import { formatDate } from 'utils/DateTimeConversion';
import Lottie from 'react-lottie';
import successAnimation from 'assets/animations/success-animation.json'; // You'll need to add this animation file
import LeadFormModal from 'components/NewLeadModal';
import QualificationModal from 'components/LeadQulificationModal';
import LeadsKanbanBoard from 'components/LeadsKanbanBoard';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import EmptyState from 'components/EmptyTable/EmptyState';
import { usePermission } from 'auth/usePermission';
import useAiRecordScores from 'components/AiAssistant/useAiRecordScores';
import { AiBulkScoreButton, aiListColumns, describeScoringSummary, withAiScores } from 'components/AiAssistant/AiListScoring';
import { useCurrency } from '../../context/CurrencyContext';
import {frontendLogger} from 'utils/logger';
import StatusBadge from 'components/StatusBadge';
import { useCurrencyCodes } from 'hooks/useCurrencyCodes';
import KanbanToolbar from 'components/KanbanToolbar';
import useKanbanFilters from 'hooks/useKanbanFilters';
import MonoText from "../../components/MonoText";
import TableActionMenu from "../../components/TableActionMenu";
import useOpenCreateFromUrl from "hooks/useOpenCreateFromUrl";
import { useTheme } from "@mui/material/styles";
import { Columns3, List, User, Users } from "lucide-react";
import PageHeader from "components/v2/PageHeader";
import SegmentedControl from "components/v2/SegmentedControl";
import { useActiveApp } from "shell-v2/apps";
import { nounFor } from "shell-v2/createActions";

const ViewToggleIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: '8px',
  backgroundColor: active ? 'white' : 'transparent',
  border: active ? 'none' : '1px solid rgba(255, 255, 255, 0.4)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: active ? 'white' : 'rgba(255, 255, 255, 0.15)',
    transform: 'translateY(-1px)',
  },
  '& svg': {
    fontSize: '20px',
    color: active ? theme.palette.info.main : 'white',
    transition: 'color 0.3s ease',
  },
  // v2: the strip is a white card header, so the toggle draws on white. The
  // repeated & outranks theme-v2's strip icon rule.
  ...(theme.pearlUi === 'v2' && {
    backgroundColor: active ? '#E6EFFB' : 'transparent',
    border: `1px solid ${active ? '#B9D0F0' : '#E2E7EF'}`,
    '&:hover': { backgroundColor: active ? '#E6EFFB' : '#F8FAFC', transform: 'none' },
    '&&&& svg': { color: `${active ? '#1453A0' : '#44546F'} !important` },
  }),
}));

const LEAD_SEARCH_FIELDS = ['id', 'name', 'email', 'phone', 'mobile', 'company', 'source', 'status', 'assigned_to_name'];

function Leads() {

  // const defaultCurrency = currencies.find(c => c.code === 'INR') || null;
  const { currency, getCurrencyLabel } = useCurrency();
  const { currencies } = useCurrencyCodes();
  const defaultCurrency = currencies.find(c => c.code === currency) || null;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const navigate = useNavigate();
  const { user, axiosInstance } = useAuth();
  const { canCreate, isSupportUser, canDelete } = usePermission('leads');
  // UI v2 layout (design-reference "Travel · Bookings list"): page header,
  // statistics row, then one card with the view switches, table and board.
  const isV2 = useTheme()?.pearlUi === 'v2';
  const { app } = useActiveApp();

  // Add these to your existing state declarations
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [allLeads, setAllLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('leadsViewMode') || 'list';
  });
  const [leadsFilter, setLeadsFilter] = useState(() => {
    return localStorage.getItem('leadsFilter') || 'my';  // Ensure 'my' is default
  });
  const [formValue, setFormValue] = useState("");
  const [agents, setAgents] = useState([]);

  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isAddLeadLoading, setIsAddLeadLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    lead: null,
    deleteRelated: false,
    loading: false,
  });

  const [statistics, setStatistics] = useState({
    total_leads: { count: 0, percentage: { amount: "0%", label: "" } },
    new_leads: { count: 0, percentage: { amount: "0%", label: "" } },
    conversion_rate: { rate: "0%", percentage: { amount: "0%", label: "" } }
  });

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    leadId: null,
    sourceStatus: '',
    destinationStatus: '',
  });

  const [qualificationModal, setQualificationModal] = useState({
    open: false,
    leadId: null,
  });

  const [updatingLeadId, setUpdatingLeadId] = useState(null);

  // Kanban rows: same as the list rows, but last_activity is the raw date
  // so date filters on it can be evaluated.
  const kanbanRows = useMemo(
      () => filteredLeads.map((l) => ({ ...l, last_activity: l.last_activity_raw })),
      [filteredLeads]
  );

  const kanbanFilters = useKanbanFilters({
    objectType: 'lead',
    rows: kanbanRows,
    searchFields: LEAD_SEARCH_FIELDS,
    enabled: viewMode === 'kanban'
  });

  const [qualificationData, setQualificationData] = useState({
    value: '0.00',  // Set default value to '0.00'
    currency: null,
    stage: 'Qualification',
    probability: 50,
    account: {
      name: '',
      is_new: true  // Set default to true
    },
    contact: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      is_new: true
    }
  });

  useEffect(() => {
    console.log(`Currency:`, currency);
    console.log(`getCurrencyLabel:`, getCurrencyLabel(currency));
  }, []);
    

  // Initialize qualification data with lead details when modal opens
  useEffect(() => {
    if (!qualificationModal.open || !qualificationModal.leadId) return;
    const lead = allLeads.find((l) => l.id === qualificationModal.leadId);
    if (!lead) return;

    // Lead's own currency first, then the tenant default
    const initialCurrency =
        currencies.find((c) => c.code === lead.currency) || defaultCurrency || null;

    setQualificationData({
      value: '0.00',
      currency: initialCurrency,
      stage: 'Qualification',
      probability: 0,
      account: { name: lead.company || '', is_new: true },
      contact: {
        first_name: lead.first_name || '',
        last_name: lead.last_name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        is_new: true,
      },
    });
    // Deliberately NOT depending on allLeads: a background refresh while the
    // modal is open must not reset what the user has already picked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualificationModal.open, qualificationModal.leadId]);

  useEffect(() => {
    if (allLeads.length > 0) {
      if (leadsFilter === 'my') {
        setFilteredLeads(allLeads.filter(lead => lead.assigned_to === user.id));
      } else {
        setFilteredLeads(allLeads);
      }
    }
  }, [leadsFilter, allLeads, user.id]);

  useEffect(() => {
    fetchLeads();
    fetchAgents();
    fetchStatistics();
  }, [leadsFilter]); // Add leadsFilter as dependency

  const handleQualificationDataChange = (event) => {
    const { name, value } = event.target;
    setQualificationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const fetchLeads = async () => {
    setIsLoading(true);
    try {
      // Add my=true query parameter when leadsFilter is 'my'
      const queryParam = leadsFilter === 'my' ? 'my=true' : 'team=true';
      const response = await axiosInstance.get(`/leads/?all=true&${queryParam}`);

      const transformedLeads = transformLeadData(response.data);
      setAllLeads(transformedLeads);

      // Apply the current filter
      if (leadsFilter === 'my') {
        setFilteredLeads(transformedLeads.filter(lead => lead.assigned_to === user.id));
      } else {
        setFilteredLeads(transformedLeads);
      }
    } catch (error) {
      console.error('Error fetching leads data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading leads. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Add this handler function
  const handleFileUpload = async (event) => {
    // Check if event and event.target exists
    if (!event?.target?.files) {
      console.error('No files selected');
      return;
    }

    const file = event.target.files[0];

    // Check if a file was selected
    if (!file) {
      setSnackbar({
        open: true,
        message: 'Please select a file to upload',
        severity: 'error'
      });
      return;
    }

    // Check file type
    if (!file.name.endsWith('.csv')) {
      setSnackbar({
        open: true,
        message: 'Please upload a CSV file',
        severity: 'error'
      });
      event.target.value = ''; // Reset file input
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosInstance.post('/leads/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      });

      setSnackbar({
        open: true,
        message: response.data?.message || 'Leads uploaded successfully',
        severity: 'success'
      });

      // Refresh the leads list
      fetchLeads();

    } catch (error) {
      console.error('Upload error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error uploading leads',
        severity: 'error'
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (event.target) {
        event.target.value = ''; // Reset file input
      }
    }
  };

  // Update the button implementation
  const BulkUploadButton = () => (
    <MDBox>
      <input
        type="file"
        id="csv-upload"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />
      <MDButton
        variant="gradient"
        color="dark"
        component="label"
        htmlFor="csv-upload"
        sx={{ mr: 2 }}
        disabled={isUploading}
      >
        {isUploading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          <>
            <Icon sx={{ fontWeight: "bold", mr: 1 }}>upload_file</Icon>
            Bulk Upload
          </>
        )}
      </MDButton>
    </MDBox>
  );

  const handleDownloadTemplate = () => {
    const headers = [
      'lead_source',
      'first_name',
      'last_name',
      'company_name',
      'title',
      'industry',
      'email',
      'phone',
      'address',
      'city',
      'state',
      'country',
      'postal_code', // treated as string in CSV
      'website',
      'annual_revenue',
      'number_of_employees',
      'notes',
      'mobile',
      'salutation',
      'designation',
      'full_lead_name'
    ];

    const sampleRow = [
      'Website',
      'John',
      'Doe',
      'Acme Inc',
      'Manager',
      'Technology',
      'john@example.com',
      '+1234567890',
      '123 Market Street',
      'San Francisco',
      'California',
      'USA',
      '94103',
      'https://acme.com',
      '5000000.00',
      '250',
      'Interested in CRM solutions',
      '+1234567890',
      'Mr.',
      'Sales Head',
      'Mr. John Doe'
    ];

    const csvContent = [
      headers.join(','),
      sampleRow.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'leads_template.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };


  const fetchStatistics = async () => {
    try {
      // Only add query parameter for team view
      const queryParam = leadsFilter === 'team' ? '?team=true' : '';
      const response = await axiosInstance.get(`/statistics/leads/${queryParam}`);
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setSnackbar({
        open: true,
        message: 'Error loading statistics. Please try again.',
        severity: 'error'
      });
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setAgents(response.data);
      console.log(`response.data for fetchAgents:`, response.data);

    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  useEffect(() => {
    const filtered = allLeads.filter(lead => {
      return Object.entries(filters).every(([key, value]) => {
        return lead[key].toLowerCase().includes(value.toLowerCase());
      });
    });
    setFilteredLeads(filtered);
  }, [filters, allLeads]);

  const transformLeadData = (serverData) => {
    return serverData.map(lead => ({
      ...lead,                          // every raw field, so filters can see them
      ...(lead.custom_fields || {}),    // flatten cf_* if the API nests them (see note below)
      id: lead.id,
      salutation: lead.salutation || "",
      first_name: lead.first_name || "",
      last_name: lead.last_name || "",
      full_name: lead.full_lead_name || "",
      name: lead.full_lead_name || "",
      company: lead.company_name || "",
      company_name: lead.company_name || "",
      source: lead.lead_source || "",
      email: lead.email || "",
      currency: lead.currency || "",
      // Frontend display field
      status: lead.lead_status || "New",
      // Raw backend field name to align with metadata.py (`lead_status`)
      lead_status: lead.lead_status || "New",
      assigned_to_name: lead.assigned_to_name || "Unassigned",
      lead_source: lead.lead_source || "Unassigned",
      assigned_to: lead.assigned_to,
      // Raw timestamp so date filters and sorting work; the column Cell formats it
      last_activity: lead?.last_activity || "",
      last_activity_raw: lead?.last_activity || "",
      mobile: lead.mobile,   // the card's phone fallback and search use it
      lead_score: lead.lead_score || 0,   // always a number
      assigned_to_avatar: lead.assigned_to_avatar,
      phone: lead.phone,
    }));
  };

  const handleOpenModal = () => {
    setIsAddLeadLoading(true); // Start loading when button is clicked
    setTimeout(() => {
      setIsModalOpen(true); // Open modal
      setIsAddLeadLoading(false); // Stop loading once modal is open
    }, 100); // Small delay to ensure UI feedback; adjust as needed
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  // v2 header "New lead": the same dialog and the same gate as Add Lead.
  useOpenCreateFromUrl(handleOpenModal, { allowed: canCreate && !isSupportUser });

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSaveLead = async (leadData) => {
    // Check if the user is a support user
    if (isSupportUser) {
      setSnackbar({
        open: true,
        message: 'Support users are not allowed to create leads.',
        severity: 'error',
      });
      return;
    }

    setIsLoading(true);
    try {
      // The modal owns the loading state and the error toast/field highlighting,
      // so this only posts and lets any error propagate.
      const response = await axiosInstance.post('/leads/', leadData);

      handleCloseModal();
      setSnackbar({
        open: true,
        message: 'Lead created successfully',
        severity: 'success',
      });

      return response.data;
    } catch (error) {
      console.error('Error creating new lead:', error);
      // No toast here: the modal maps the error and shows it.
      // Rethrow so the modal stays open and can highlight fields.
      throw error;
    } finally {
      setIsLoading(false);
      setFormValue({});
    }
  };

  const handleRowClick = (rowData) => {
    navigate(`/leads/${rowData.id}`);
  };

  const handleViewModeChange = (newMode) => {
    setViewMode(newMode);
    localStorage.setItem('leadsViewMode', newMode);
  };

  const handleAgentClick = (agentId) => {
    if (agentId) {
      navigate(`/users/${agentId}/profile`);
    } else {
      setSnackbar({
        open: true,
        message: 'This lead is not assigned to any agent.',
        severity: 'info',
      });
    }
  };

  const handleDeleteClick = (lead) => {
    if (isSupportUser) {
      setSnackbar({ open: true, message: 'Support users are not allowed to delete leads.', severity: 'error' });
      return;
    }
    setDeleteDialog({ open: true, lead, deleteRelated: false, loading: false });
  };

  const handleDeleteCancel = () => {
    if (deleteDialog.loading) return; // don't close mid-request
    setDeleteDialog((d) => ({ ...d, open: false }));
  };

  const handleDeleteConfirm = async () => {
    const { lead, deleteRelated } = deleteDialog;
    if (!lead) return;
    if (isSupportUser) {
      setSnackbar({ open: true, message: 'Support users are not allowed to delete leads.', severity: 'error' });
      return;
    }

    setDeleteDialog((d) => ({ ...d, loading: true }));
    try {
      await axiosInstance.delete(`/leads/${lead.id}`, {
        params: { delete_related: deleteRelated },
      });

      // A logging failure must not make a successful delete look like a failure
      try {
        await frontendLogger.logDelete('Lead', {
          id: lead.id,
          name: lead.name || lead.full_name,
          user: user.username,
          deleted_by: user.id,
        });
      } catch (logError) {
        console.error('Error logging lead deletion:', logError);
      }

      // Remove from the list without refetching
      setAllLeads((prev) => prev.filter((l) => l.id !== lead.id));
      setFilteredLeads((prev) => prev.filter((l) => l.id !== lead.id));
      fetchStatistics(); // keep the stat cards in sync

      setDeleteDialog({ open: false, lead: null, deleteRelated: false, loading: false });
      setSnackbar({ open: true, message: 'Lead deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('Error deleting lead:', error);
      try {
        await frontendLogger.logError(error, {
          context: 'Lead deletion',
          leadId: lead.id,
          user: user?.username,
          action: 'delete',
          timestamp: new Date().toISOString(),
        });
      } catch (logError) {
        console.error('Error logging deletion failure:', logError);
      }

      const detail = error.response?.data?.detail;
      setSnackbar({
        open: true,
        message: typeof detail === 'string' ? detail : 'Error deleting lead. Please try again.',
        severity: 'error',
      });
      // Keep the dialog open so the user can retry or cancel
      setDeleteDialog((d) => ({ ...d, loading: false }));
    }
  };

  // AI Assistant scores: an optional column, and "Score with AI" on a selection.
  const leadRowIds = useMemo(() => filteredLeads.map((l) => l.id), [filteredLeads]);
  const aiScores = useAiRecordScores('leads', leadRowIds);
  const [aiSelected, setAiSelected] = useState([]);
  const leadRows = useMemo(() => withAiScores(filteredLeads, aiScores), [filteredLeads, aiScores.enabled, aiScores.scores]);

  const leadsTableData = useMemo(() => ({
    columns: [
      ...aiListColumns({ ai: aiScores, selected: aiSelected, setSelected: setAiSelected, rowIds: leadRowIds }),
      {
        Header: "ID",
        accessor: "id",
        width: "5%",
        Cell: ({ row }) => (
            <MDTypography
                component="span"
                onClick={() => handleRowClick(row.original)}
                variant="caption"
                color="info"
                fontWeight="medium"
                sx={{ cursor: 'pointer' }}
            >
              {`#${row.original.id}`}
            </MDTypography>
        )
      },
      {
        Header: "Name",
        accessor: "name",
        width: "10%",
        Cell: ({ row }) => {
          // The name is this row's only way into the record, and the click
          // handler sits on the text itself -- so a lead with no name rendered
          // an empty span with nothing to click, stranding the record in the
          // list. Fall back to something identifying, and never to "".
          const named = Boolean(row.original.name);
          const label =
              row.original.name ||
              row.original.email ||
              row.original.company ||
              `Lead ${row.original.id}`;
          return (
              <MDTypography
                  component="span"
                  onClick={() => handleRowClick(row.original)}
                  variant="caption"
                  color="info"
                  fontWeight="medium"
                  sx={{ cursor: 'pointer', fontStyle: named ? 'normal' : 'italic' }}
                  title={named ? undefined : 'This lead has no name yet'}
              >
                {label}
              </MDTypography>
          );
        }
      },
      {
        Header: "Email",
        accessor: "email",
        width: "14%",
        Cell: ({ value }) => (
            <MDTypography variant="caption" fontWeight="bold">
              {value}
            </MDTypography>
        ),
      },
      // Hidden from core CRM UI (column kept for data mapping / re-enable)
      {
        Header: "Source",
        accessor: "source",
        width: "8%",
        Cell: ({ value }) => (
          <MDTypography variant="caption">
            {value || "-"}
          </MDTypography>
        ),
      },
      {
        Header: "Assigned Agent",
        accessor: "assigned_to_name",
        width: "15%",
        Cell: ({ value, row }) => (
            <MDTypography
                component="span"
                onClick={() => handleAgentClick(row.original.assigned_to)}
                variant="caption"
                color="info"
                fontWeight="medium"
                sx={{
                  cursor: 'pointer'
                }}
            >
              {value || '-'}
            </MDTypography>
        )
      },
      {
        Header: "Last Activity",
        accessor: "last_activity",
        width: "15%",
        Cell: ({ value }) => <MonoText>{value ? formatDate(value) : '-'}</MonoText>,
      },
      { Header: "Status", accessor: "status", width: "8%", Cell: ({ value }) => <StatusBadge size="xs" status={value} label={value} /> },
      {
        Header: "Actions",
        accessor: "actions",
        width: "5%",
        align: "center",
        disableSortBy: true,
        Cell: ({ row }) => (
            (
              canDelete
                ? <TableActionMenu row={row.original} onView={handleRowClick} onDelete={() => handleDeleteClick(row.original)}/>
                : <TableActionMenu row={row.original} onView={handleRowClick}/>
            )
        ),
      },
    ],
    rows: leadRows,
  }), [filteredLeads, leadRows, agents, canDelete, aiScores.enabled, aiScores.canScore, aiScores.scores, aiSelected, leadRowIds]);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      setUpdatingLeadId(leadId); // Show loading state

      // Optimistically update the UI
      const updatedLeads = allLeads.map(lead => {
        if (lead.id === leadId) {
          return { ...lead, status: newStatus };
        }
        return lead;
      });
      setAllLeads(updatedLeads);

      // Apply current filter
      if (leadsFilter === 'my') {
        setFilteredLeads(updatedLeads.filter(lead => lead.assigned_to === user.id));
      } else {
        setFilteredLeads(updatedLeads);
      }

      // Make API call
      await axiosInstance.patch(`/leads/${leadId}`, { lead_status: newStatus });

      // Log lead status update
      const lead = allLeads.find(l => l.id === leadId);
      await frontendLogger.logUpdate('Lead', leadId, {
        action: 'status_update',
        old_status: lead?.status,
        new_status: newStatus,
        updated_by: user.id,
        updated_at: new Date().toISOString(),
        lead_name: lead?.name,
        company: lead?.company,
        user: user?.username,
        timestamp: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: `Lead status updated to ${newStatus}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating lead status:', error);

      // Log error
      await frontendLogger.logError(error, {
        context: 'Lead status update',
        leadId: leadId,
        newStatus: newStatus,
        user: user?.username,
        action: 'status_update',
        timestamp: new Date().toISOString(),
      });

      // Revert the optimistic update on error
      const revertedLeads = allLeads.map(lead => {
        if (lead.id === leadId) {
          return { ...lead, status: lead.status }; // Revert to original status
        }
        return lead;
      });
      setAllLeads(revertedLeads);
      setFilteredLeads(leadsFilter === 'my' ?
        revertedLeads.filter(lead => lead.assigned_to === user.id) :
        revertedLeads
      );

      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error updating lead status',
        severity: 'error',
      });
      throw error;
    } finally {
      setUpdatingLeadId(null); // Clear loading state
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const sourceStatus = result.source.droppableId;
    const destinationStatus = result.destination.droppableId;
    const leadId = parseInt(result.draggableId);

    const draggedLead = allLeads.find(lead => lead.id === leadId);

    if (['Qualified', 'Disqualified'].includes(draggedLead.status)) {
      setSnackbar({
        open: true,
        message: `Cannot move ${draggedLead.status} leads`,
        severity: 'warning',
      });
      return;
    }

    if (['Qualified', 'Disqualified'].includes(destinationStatus)) {
      setConfirmationDialog({
        open: true,
        leadId,
        sourceStatus,
        destinationStatus,
      });
      return;
    }

    try {
      await updateLeadStatus(leadId, destinationStatus);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error updating lead status',
        severity: 'error',
      });
    }
  };

  const handleStatusConfirmation = async (confirmed) => {
    const { leadId, destinationStatus } = confirmationDialog;
    setConfirmationDialog({ ...confirmationDialog, open: false });

    if (confirmed) {
      try {
        if (destinationStatus === 'Qualified') {
          // Show qualification modal
          setQualificationModal({
            open: true,
            leadId,
          });
        } else {
          // Handle disqualification
          await updateLeadStatus(leadId, destinationStatus);
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.response?.data?.detail || 'Error updating lead status',
          severity: 'error',
        });
      }
    }
  };

  const formatApiError = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      return detail
          .map((d) => `${(d.loc || []).filter((p) => p !== 'body').join('.')}: ${d.msg}`)
          .join(' | ');
    }
    if (typeof detail === 'string') return detail;
    return fallback;
  };

  const handleQualificationSuccess = async (qualificationData) => {
    const { leadId } = qualificationModal;
    setQualificationModal({ open: false, leadId: null });

    try {
      // Prepare conversion data
      const conversionData = {
        lead_id: leadId,
        value: Number(qualificationData.value) || 0,
        currency: qualificationData.currency,                       // already a code string
        stage: qualificationData.stage,
        probability: Number.parseInt(qualificationData.probability, 10) || 0,
        estimated_closure_date: new Date(qualificationData.estimated_closure_date).toISOString(),
        assigned_to: qualificationData.assigned_to ?? null,

        // Account handling
        create_new_account: qualificationData.account.is_new,
        existing_account_id: qualificationData.account.is_new ? null : qualificationData.account.id,
        new_account_name: qualificationData.account.is_new ? qualificationData.account.name : null,

        // Contact handling
        create_new_contact: qualificationData.contact.is_new,
        existing_contact_id: qualificationData.contact.is_new ? null : qualificationData.contact.id,
        new_contact_first_name: qualificationData.contact.first_name,
        new_contact_last_name: qualificationData.contact.last_name,
      };

      // Call the conversion API
      const response = await axiosInstance.post(`/leads/${leadId}/convert`, conversionData);

      // Update the leads list
      const updatedLeads = allLeads.map(lead => {
        if (lead.id === leadId) {
          return { ...lead, status: 'Qualified' };
        }
        return lead;
      });
      setAllLeads(updatedLeads);
      setFilteredLeads(leadsFilter === 'my' ?
        updatedLeads.filter(lead => lead.assigned_to === user.id) :
        updatedLeads
      );
      let message = 'Lead converted to Deal successfully';
      
      setSnackbar({
        open: true,
        message: message,
        severity: 'success',
      });

      // Navigate to the newly created deal
      navigate(`/deals/${response.data.id}`);

    } catch (error) {
      console.error('Error qualifying lead:', error);
      setSnackbar({
        open: true,
        message: formatApiError(error, 'Error qualifying lead. Please try again.'),
        severity: 'error',
      });
    }
  };

  const handleQualificationCancel = () => {
    setQualificationModal({ open: false, leadId: null });
  };

  const handleLeadsFilterChange = async (newFilter) => {
    if (newFilter === leadsFilter) return;
    setLeadsFilter(newFilter);
    localStorage.setItem('leadsFilter', newFilter);
    setIsLoading(true);

    try {
      const queryParam = newFilter === 'my' ? '?all=true&my=true' : '?all=true&team=true';
      const response = await axiosInstance.get(`/leads/${queryParam}`);

      const transformedLeads = transformLeadData(response.data);
      setAllLeads(transformedLeads);

      if (newFilter === 'my') {
        setFilteredLeads(transformedLeads.filter(lead => lead.assigned_to === user.id));
      } else {
        setFilteredLeads(transformedLeads);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      setSnackbar({
        open: true,
        message: 'Error loading leads. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Shared by the classic and v2 layouts below, so both render the same
  // statistics, actions, table and board.
  const kpiCards = [
    <ComplexStatisticsCard
      key="kpi-0"
      color="dark"
      icon="leaderboard"
      title="Total Leads"
      count={statistics.total_leads.count}
      percentage={{
        color: parseFloat(statistics.total_leads.percentage.amount) >= 0 ? "success" : "error",
        amount: statistics.total_leads.percentage.amount,
        label: statistics.total_leads.percentage.label,
      }}
    />,
    <ComplexStatisticsCard
      key="kpi-1"
      icon="person_add"
      title="New Leads"
      count={statistics.new_leads.count}
      percentage={{
        color: parseFloat(statistics.new_leads.percentage.amount) >= 0 ? "success" : "error",
        amount: statistics.new_leads.percentage.amount,
        label: statistics.new_leads.percentage.label,
      }}
    />,
    <ComplexStatisticsCard
      key="kpi-2"
      color="success"
      icon="store"
      title="Conversion Rate"
      count={statistics.conversion_rate.rate}
      percentage={{
        color: parseFloat(statistics.conversion_rate.percentage.amount) >= 0 ? "success" : "error",
        amount: statistics.conversion_rate.percentage.amount,
        label: statistics.conversion_rate.percentage.label,
      }}
    />,
  ];
  const leadActions = (
    <>
      {/* // Add this button next to the Bulk Upload button */}
      <Tooltip title="Download CSV Template">
        <MDButton
          variant="gradient"
          color="dark"
          component="label"
          onClick={handleDownloadTemplate}
          sx={{ mr: 2 }}
        >
          <Icon sx={{ fontWeight: "bold", mr: 1 }}>download</Icon>
          Template
        </MDButton>
      </Tooltip>

      {/* Bulk Upload Button */}
      <BulkUploadButton />

      {viewMode === 'list' && (
        <AiBulkScoreButton
          ai={aiScores}
          selected={aiSelected}
          onDone={(summary) => {
            setAiSelected([]);
            setSnackbar({ open: true, message: describeScoringSummary(summary), severity: summary.message ? 'warning' : 'success' });
          }}
        />
      )}

      {canCreate && !isSupportUser && (
          <MDButton
              variant="gradient"
              color="dark"
              onClick={handleOpenModal}
              sx={{ mr: 2 }}
              disabled={isAddLeadLoading} // Disable while loading
          >
            {isAddLeadLoading ? (
                <CircularProgress size={24} color="inherit" />
            ) : (
                <>
                  <Icon sx={{ fontWeight: "bold", mr: 1 }}>add</Icon>
                  Add Lead
                </>
            )}
          </MDButton>
      )}
    </>
  );
  const leadsBody = (
    <>
      {!isLoading ? (
        viewMode === 'list' ? (
          filteredLeads.length > 0 ? (
            <DataTable
              table={leadsTableData}
              isSorted={true}
              entriesPerPage={{
                defaultValue: 10,
                entries: [5, 10, 15, 20, 25],
              }}
              canSearch
              noEndBorder
              showTotalEntries
              pagination={{ variant: "gradient", color: "info" }}
              searchPlaceholder="Search Lead"
              exportCSV={true}
              objectType="lead"
              onFilterApply={(conditions) => {
                console.log('Filter applied:', conditions);
                // Optionally refetch leads with filter conditions
              }}
              onFilterClear={() => {
                console.log('Filter cleared');
                fetchLeads();
              }}
            />
          ) : (
            <EmptyState
              moduleName="Lead"
              filterType={leadsFilter}
              canCreate={canCreate}
            />
          )
        ) : (
            filteredLeads.length > 0 ? (
                <>
                  <KanbanToolbar
                      filters={kanbanFilters}
                      searchPlaceholder="Search Lead"
                      itemLabel="lead"
                      objectLabel="Leads"
                  />
                  {kanbanFilters.filtersLoading ? (
                    <TableSkeleton />
                  ) : kanbanFilters.visibleRows.length > 0 ? (
                      <LeadsKanbanBoard
                          leads={kanbanFilters.visibleRows}
                          onDragEnd={handleDragEnd}
                          onLeadClick={(lead) => handleRowClick(lead)}
                          updatingLeadId={updatingLeadId}
                      />
                  ) : (
                      <MDBox py={8} textAlign="center">
                        <MDTypography variant="h6">No leads match your filters</MDTypography>
                        <MDButton variant="outlined" color="info" size="small" sx={{ mt: 2 }} onClick={kanbanFilters.clearAll}>
                          Clear filters
                        </MDButton>
                      </MDBox>
                  )}
                </>
            ) : (
                <EmptyState moduleName="Lead" filterType={leadsFilter} canCreate={canCreate} />
            )
        )
      ) : (
        <TableSkeleton />
      )}
    </>
  );

  const leadsLabel = app?.labels?.leads || 'Leads';
  const leadNoun = nounFor({ key: 'leads', noun: 'lead' }, app);
  const leadCount = filteredLeads.length;
  const v2Main = (
    <MDBox>
      <PageHeader
        title={leadsLabel}
        summary={
          isLoading
            ? null
            : `${leadCount} ${leadCount === 1 ? leadNoun : leadsLabel.toLowerCase()} ${
                leadsFilter === 'my' ? 'assigned to you' : 'across your team'
              }`
        }
        actions={leadActions}
      />
      <Grid container spacing={2} mb={2.5}>
        {kpiCards.map((cardEl) => (
          <Grid item xs={12} md={4} key={cardEl.key}>
            {cardEl}
          </Grid>
        ))}
      </Grid>
      <Card>
        <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1.5} px={2.5} pt={2.5}>
          <SegmentedControl
            label={`Whose ${leadsLabel.toLowerCase()}`}
            value={leadsFilter}
            onChange={handleLeadsFilterChange}
            options={[
              { value: 'my', label: 'Mine', icon: User },
              { value: 'team', label: 'My team', icon: Users },
            ]}
          />
          <SegmentedControl
            label="View"
            value={viewMode}
            onChange={handleViewModeChange}
            options={[
              { value: 'list', label: 'List', icon: List },
              { value: 'kanban', label: 'Board', icon: Columns3 },
            ]}
          />
        </MDBox>
        <MDBox pt={1}>{leadsBody}</MDBox>
      </Card>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isV2 ? v2Main : (
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              {kpiCards[0]}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              {kpiCards[1]}
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              {kpiCards[2]}
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={4.5}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <MDTypography variant="h6" color="white">
                      Leads
                    </MDTypography>

                    {/* Leads Filter Toggle */}
                    <MDBox display="flex" gap={1} mr={2}>
                      <Tooltip title="My Leads" placement="bottom">
                        <ViewToggleIcon
                          active={leadsFilter === 'my'}
                          onClick={() => handleLeadsFilterChange('my')}
                        >
                          <Icon>person</Icon>
                        </ViewToggleIcon>
                      </Tooltip>
                      <Tooltip title="My Team's Leads" placement="bottom">
                        <ViewToggleIcon
                          active={leadsFilter === 'team'}
                          onClick={() => handleLeadsFilterChange('team')}
                        >
                          <Icon>groups</Icon>
                        </ViewToggleIcon>
                      </Tooltip>
                    </MDBox>

                    {/* View Mode Toggle */}
                    <MDBox display="flex" gap={1}>
                      <Tooltip title="List View" placement="bottom">
                        <ViewToggleIcon
                          active={viewMode === 'list'}
                          onClick={() => handleViewModeChange('list')}
                        >
                          <ViewListIcon />
                        </ViewToggleIcon>
                      </Tooltip>
                      <Tooltip title="Kanban View" placement="bottom">
                        <ViewToggleIcon
                          active={viewMode === 'kanban'}
                          onClick={() => handleViewModeChange('kanban')}
                        >
                          <ViewKanbanIcon />
                        </ViewToggleIcon>
                      </Tooltip>
                    </MDBox>

                  </MDBox>
                  <MDBox display="flex" alignItems="center">
                    {leadActions}
                  </MDBox>
                </MDBox>
                <MDBox pt={3}>
                    {leadsBody}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      )}

      <LeadFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveLead}
        agents={agents}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
        user={user}
      />

      {showSuccessAnimation && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
          }}
        >
          <Lottie
            options={{
              loop: false,
              autoplay: true,
              animationData: successAnimation,
              rendererSettings: {
                preserveAspectRatio: 'xMidYMid slice'
              }
            }}
            height={200}
            width={200}
          />
        </Box>
      )}


      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmationDialog.open}
        onClose={() => handleStatusConfirmation(false)}
      >
        <DialogTitle>
          {confirmationDialog.destinationStatus === 'Qualified' ? 'Qualify Lead' : 'Disqualify Lead'}
        </DialogTitle>
        <DialogContent>
          {confirmationDialog.destinationStatus === 'Qualified'
            ? "You'll need to provide additional information to qualify this lead."
            : "Are you sure you want to disqualify this lead? This action will lock the lead for further status changes."}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => handleStatusConfirmation(false)} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={() => handleStatusConfirmation(true)} color="primary">
            {confirmationDialog.destinationStatus === 'Qualified' ? 'Continue' : 'Confirm'}
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isUploading}
        PaperProps={{
          style: {
            minWidth: '300px',
            padding: '20px'
          }
        }}
      >
        <DialogTitle>Uploading Leads</DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              sx={{ height: 10, borderRadius: 5 }}
            />
            <MDTypography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mt: 1 }}
            >
              {uploadProgress}%
            </MDTypography>
          </Box>
        </DialogContent>
      </Dialog>

      <QualificationModal
        isOpen={qualificationModal.open}
        leadId={qualificationModal.leadId}
        qualificationData={qualificationData}
        handleQualificationDataChange={handleQualificationDataChange}
        handleQualificationConfirm={handleQualificationSuccess}
        onClose={handleQualificationCancel}
        lead={allLeads.find(lead => lead.id === qualificationModal.leadId)}
        axiosInstance={axiosInstance}
      />

      <Modal
          open={deleteDialog.open}
          onClose={deleteDialog.loading ? undefined : handleDeleteCancel}
          aria-labelledby="delete-lead-title"
      >
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400, bgcolor: 'background.paper', boxShadow: 24,
          borderRadius: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <Box className="pearl-popup-header" sx={{ backgroundColor: colors.popupHeader.background, px: 4, py: 3 }}>
            <MDTypography id="delete-lead-title" variant="h6" component="h2" sx={{ color: '#FFFFFF' }}>
              Delete Lead
            </MDTypography>
          </Box>

          <Box sx={{ p: 4 }}>
            <MDTypography variant="body1" mb={3} sx={{ color: '#34495e' }}>
              Are you sure you want to delete{' '}
              <strong>{deleteDialog.lead?.name || deleteDialog.lead?.email || `Lead ${deleteDialog.lead?.id ?? ''}`}</strong>?
              This action cannot be undone.
            </MDTypography>

            <FormControlLabel
                control={
                  <Checkbox
                      checked={deleteDialog.deleteRelated}
                      onChange={(e) => setDeleteDialog((d) => ({ ...d, deleteRelated: e.target.checked }))}
                      color="primary"
                      disabled={deleteDialog.loading}
                  />
                }
                label="Delete related records"
            />

            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton
                  onClick={handleDeleteCancel}
                  variant="outlined"
                  color="secondary"
                  disabled={deleteDialog.loading}
                  sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                  onClick={handleDeleteConfirm}
                  variant="gradient"
                  color="error"
                  disabled={deleteDialog.loading}
              >
                {deleteDialog.loading ? <CircularProgress size={22} color="inherit" /> : 'Delete'}
              </MDButton>
            </MDBox>
          </Box>
        </Box>
      </Modal>

    </DashboardLayout>
  );
}

export default Leads;
